import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Target, Crown, Star, TrendingUp, Zap, Users } from 'lucide-react';
import { heroData } from '../data/mock';
import { TidalCursor } from './ui/tidal-cursor';

const StatItem = ({ value, label }) =>
<div className="flex flex-col items-center justify-center transition-transform hover:-translate-y-1 cursor-default">
    <span className="text-lg font-bold text-white sm:text-xl">{value}</span>
    <span className="text-[9px] uppercase tracking-wider text-zinc-400 font-medium sm:text-[10px]">{label}</span>
  </div>;


const Hero = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleBookCall = () => {
    // Open Calendly in a popup window
    window.open(
      'https://calendly.com/esaezye',
      'calendly',
      'width=800,height=800,scrollbars=yes,resizable=yes'
    );
  };

  return (
    <section id="inicio" className="relative w-full bg-[#013139] text-white overflow-hidden">
      {/* Tidal Cursor Effect */}
      <TidalCursor />
      
      {/* Scoped Animations */}
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeSlideIn 0.8s ease-out forwards;
          opacity: 0;
        }
        .delay-100 { animation-delay: 0.1s; }
        .delay-200 { animation-delay: 0.2s; }
        .delay-300 { animation-delay: 0.3s; }
        .delay-400 { animation-delay: 0.4s; }
        .delay-500 { animation-delay: 0.5s; }
      `}</style>

      {/* Background Image with Gradient Mask */}
      <div
        className="absolute inset-0 z-0 bg-cover bg-center opacity-30"
        style={{
          backgroundImage: `url(${heroData.backgroundImage})`,
          maskImage: "linear-gradient(180deg, transparent, black 0%, black 70%, transparent)",
          WebkitMaskImage: "linear-gradient(180deg, transparent, black 0%, black 70%, transparent)"
        }} />


      {/* Animated circles decoration */}
      <div className="absolute inset-0 z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-72 h-72 bg-[#00E5A3]/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-[#00C497]/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      <div className="relative z-20 mx-auto max-w-7xl px-4 pt-32 pb-16 sm:px-6 md:pt-40 md:pb-24 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8 items-start">
          
          {/* --- LEFT COLUMN --- */}
          <div className="lg:col-span-8 flex flex-col justify-center space-y-8 pt-8">
            
            {/* Badge - sin logo duplicado */}
            <div className="animate-fade-in delay-100">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 backdrop-blur-md transition-colors hover:bg-white/20">
                <span className="text-xs font-semibold uppercase tracking-wider text-white flex items-center gap-2">
                  25+ Años de Experiencia
                  <Star className="w-4 h-4 text-[#00E5A3] fill-[#00E5A3]" />
                </span>
              </div>
            </div>

            {/* Heading */}
            <h1
              className="animate-fade-in delay-200 text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold tracking-tight leading-[0.95]"
              style={{
                maskImage: "linear-gradient(180deg, black 0%, black 80%, transparent 100%)",
                WebkitMaskImage: "linear-gradient(180deg, black 0%, black 80%, transparent 100%)"
              }}>

              Tu negocio puede ir<br />
              <span className="bg-gradient-to-br from-white via-[#00E5A3] to-[#00C497] bg-clip-text text-transparent">
                más rápido.
              </span>
            </h1>

            {/* Description */}
            <p className="animate-fade-in delay-300 max-w-xl text-xl text-gray-200 leading-relaxed">
              Yo te enseño cómo sin complicaciones. Automatización con IA de forma cercana, 
              sin tecnicismos y con resultados medibles desde el primer día.
            </p>

            {/* CTA Buttons */}
            <div className="animate-fade-in delay-400 flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleBookCall}
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#00E5A3] px-8 py-4 text-base font-bold text-[#013139] transition-all hover:scale-[1.02] hover:bg-[#00C497] active:scale-[0.98] shadow-lg shadow-[#00E5A3]/20">

                Reserva tu llamada gratuita
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </button>
              
              <button
                onClick={() => scrollToSection('servicios')}
                className="group inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/20 bg-white/10 px-8 py-4 text-base font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/20 hover:border-white/40">

                Conoce los servicios
              </button>
            </div>

            {/* Trust indicators */}
            <div className="animate-fade-in delay-500 flex flex-wrap gap-6 text-white/80">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-[#00E5A3]" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
                </svg>
                <span className="text-sm font-medium">Sin compromiso</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-[#00E5A3]" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
                </svg>
                <span className="text-sm font-medium">Resultados garantizados</span>
              </div>
            </div>
          </div>

          {/* --- RIGHT COLUMN --- */}
          <div className="lg:col-span-4 space-y-4 lg:mt-16 lg:ml-auto lg:max-w-sm">
            
            {/* Stats Card - Mismo tamaño que Por qué FluenceIA */}
            <div className="animate-fade-in delay-500 relative overflow-hidden border border-white/20 backdrop-blur-xl p-5 !rounded-[14px] !shadow-lg bg-white/10">
              {/* Card Glow Effect */}
              <div className="absolute top-0 right-0 -mr-8 -mt-8 h-32 w-32 rounded-full bg-[#00E5A3]/10 blur-2xl pointer-events-none" />

              <div className="relative z-10">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#00E5A3]/20 ring-1 ring-[#00E5A3]/40">
                    <Target className="h-4 w-4 text-[#00E5A3]" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold tracking-tight text-white">100+</div>
                    <div className="text-[10px] text-gray-300">Empresarios Asesorados</div>
                  </div>
                </div>

                {/* Progress Bar Section */}
                <div className="space-y-1.5 mb-4">
                  <div className="flex justify-between text-[10px] !bg-[rgba(255,255,255,0)]">
                    <span className="text-gray-300">Satisfacción</span>
                    <span className="text-white font-semibold">98%</span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                    <div className="h-full w-[98%] rounded-full bg-gradient-to-r from-[#00E5A3] to-[#00C497] shadow-md shadow-[#00E5A3]/20" />
                  </div>
                </div>

                <div className="h-px w-full bg-white/20 mb-3" />

                {/* Mini Stats Grid */}
                <div className="grid grid-cols-3 gap-2 text-center mb-3">
                  <div className="flex flex-col items-center">
                    <span className="text-sm font-bold text-white">25+</span>
                    <span className="text-[8px] uppercase tracking-wider text-zinc-400 font-medium">Años</span>
                  </div>
                  <div className="w-px h-full bg-white/20 mx-auto" />
                  <div className="flex flex-col items-center">
                    <span className="text-sm font-bold text-white">50+</span>
                    <span className="text-[8px] uppercase tracking-wider text-zinc-400 font-medium">Proyectos</span>
                  </div>
                  <div className="w-px h-full bg-white/20 mx-auto" />
                  <div className="flex flex-col items-center">
                    <span className="text-sm font-bold text-white">100%</span>
                    <span className="text-[8px] uppercase tracking-wider text-zinc-400 font-medium">Calidad</span>
                  </div>
                </div>

                {/* Tag Pills */}
                <div className="flex flex-wrap gap-1.5">
                  <div className="inline-flex items-center gap-1 rounded-full border border-[#00E5A3]/30 bg-[#00E5A3]/10 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-[#00E5A3]">
                    <span className="relative flex h-1 w-1">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00E5A3] opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-1 w-1 bg-[#00E5A3]"></span>
                    </span>
                    DISPONIBLE
                  </div>
                  <div className="inline-flex items-center gap-1 rounded-full border border-white/20 bg-white/10 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-white">
                    <Crown className="w-2.5 h-2.5 text-yellow-400" />
                    EXPERTO
                  </div>
                </div>
              </div>
            </div>

            {/* Benefits Card - Más grande */}
            <div className="animate-fade-in delay-500 relative overflow-hidden border border-white/20 backdrop-blur-xl p-5 !rounded-[14px] !shadow-lg bg-white/10">
              <h3 className="uppercase !font-bold !text-xs mb-3 text-gray-200">Por qué FluenceIA</h3>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#00E5A3]/20 flex-shrink-0">
                    <TrendingUp className="h-4 w-4 text-[#00E5A3]" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white leading-tight mb-0.5">Experiencia Real</p>
                    <p className="text-xs text-gray-300 leading-relaxed">25+ años en banca ayudando empresarios</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#00E5A3]/20 flex-shrink-0">
                    <Zap className="h-4 w-4 text-[#00E5A3]" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white leading-tight mb-0.5">Resultados Rápidos</p>
                    <p className="text-xs text-gray-300 leading-relaxed">Automatizaciones en 2-4 semanas</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#00E5A3]/20 flex-shrink-0">
                    <Users className="h-4 w-4 text-[#00E5A3]" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white leading-tight mb-0.5">Cercano y Claro</p>
                    <p className="text-xs text-gray-300 leading-relaxed">Sin tecnicismos, en tu idioma</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-white/50 rounded-full" />
        </div>
      </div>
    </section>
  );
};

export default Hero;